/* eslint-disable no-param-reassign */
import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_CONFIGURATION, STEPS } from "../../../Common/store/data/commonData";
import {
  setCurrentStepImplementation,
  setSavedConfigurationsImplementation
} from "../../../Common/store/implementations/commonImplementation";
import t from "../../../Common/utils/translation/translator";

const initialState = {
  configuration: null,
  filtersInit: null,
  possibleFiltersValues: null,
  defaultConfiguration: {
    ...DEFAULT_CONFIGURATION,
    technology: "INTEGRATED_DRIVES",
    configurationType: "INTEGRATED_DRIVES",
    axes: []
  },
  steps: STEPS.INTEGRATED_DRIVES,
  currentStep: STEPS.INTEGRATED_DRIVES.START,
  readOnlyMode: false,
  savedConfigurations: []
};

export const integrated_drives = createSlice({
  name: "integrated_drives",
  initialState,
  reducers: {
    // LOGIC
    setIntegratedDrivesConfiguration: (state, action) => {
      state.configuration = action.payload;
      // set currentStepId
      const stepsAsArray = Object.values(state.steps);
      // eslint-disable-next-line no-param-reassign
      state.currentStep = stepsAsArray.find((step) => step.id === action.payload.configData.currentStepId);
    },
    resetIntegratedDrives: (state) => {
      const resetState = { ...initialState };
      resetState.possibleFiltersValues = state.possibleFiltersValues;
      resetState.defaultConfiguration = state.defaultConfiguration;
      resetState.filtersInit = state.filtersInit;
      return resetState;
    },
    setIntegratedDrivesPossibleFiltersValues: (state, action) => {
      const possibleFiltersValuesFromS3 = action.payload;
      // eslint-disable-next-line no-param-reassign
      state.possibleFiltersValues = possibleFiltersValuesFromS3;

      // set filtersInit
      const filtersInitFromPossibleValues = {
        integratedDriveFilter: {},
        gearboxFilter: {
          enabled: false,
          gearboxType: [],
          ratio: [],
          efficiency: [],
          shaftMomentOfInertia: {
            min: possibleFiltersValuesFromS3.gearbox.shaftMomentOfInertia.min,
            max: possibleFiltersValuesFromS3.gearbox.shaftMomentOfInertia.max
          },
          continuousOutputTorque: {
            min: possibleFiltersValuesFromS3.gearbox.continuousOutputTorque.min,
            max: possibleFiltersValuesFromS3.gearbox.continuousOutputTorque.max
          },
          family: [],
          // productOrComponentType: ["PLANETARY_GEARBOX"],
          productOrComponentType: [],
          inputDiameter: [],
          maximumOutputTorque: {
            min: possibleFiltersValuesFromS3.gearbox.maximumOutputTorque.min,
            max: possibleFiltersValuesFromS3.gearbox.maximumOutputTorque.max
          },
          torsionalBacklash: {
            min: 0,
            max: Math.max(...possibleFiltersValuesFromS3.gearbox.torsionalBacklash)
          }
        }
      };
      // eslint-disable-next-line no-param-reassign
      state.filtersInit = filtersInitFromPossibleValues;
    },
    setIntegratedDrivesCurrentStep: (state, action) => {
      setCurrentStepImplementation(state, action);
    },
    setIntegratedDrivesConfigData: (state, action) => {
      const { configuration } = state;
      // eslint-disable-next-line no-param-reassign
      configuration.configData = action.payload;
    },
    setReadOnlyMode: (state, action) => {
      state.readOnlyMode = action.payload;
    },
    setIntegratedDrivesSavedConfigurations: (state, action) => {
      setSavedConfigurationsImplementation(state, action);
    },

    setIntegratedDrivesAxes: (state, action) => {
      state.configuration.configData.axes = action.payload;
    },

    addNewIntegratedDrivesAxis: (state) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration } = state;
      const { filtersInit } = state;
      const newId = configuration?.configData?.axes?.reduce((max, axis) => Math.max(max, axis.id), 0) + 1 || 1;

      // if applicaton settings (configData.applicationSettings) are defined =>
      // set nbPhases, driveVoltages, driveInterface into driveFilters (in filtersInit)

      const newAxis = {
        id: newId,
        name: `${t("msizer.ui.axis")} ${newId}`,
        motorType: null,
        configFilters: {
          ...filtersInit
        },
        collapsed: true
      };

      configuration?.configData?.axes?.push(newAxis);
      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    // Update Axis infos
    updateAxisName: (state, action) => {
      const { axisId, name } = action.payload;
      const { configuration } = state;
      const axis = configuration?.configData?.axes?.find((a) => a.id === axisId);

      if (axis) {
        axis.name = name;
        state.configuration = {
          ...configuration,
          configData: {
            ...configuration.configData,
            axes: configuration.configData.axes.map((a) => (a.id === axisId ? axis : a))
          }
        };
      }
    },

    setIntegratedDrivesAxisFilters: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration } = state;
      const { axisId, filters } = action.payload;

      if (!axisId) {
        return;
      }

      configuration.configData.axes.find((axis) => axis.id === axisId).configFilters = filters;
      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    collapseAxis: (state, action) => {
      const { axisId } = action.payload;
      const { configuration } = state;
      const axis = configuration?.configData?.axes?.find((a) => a.id === axisId);

      if (axis) {
        axis.collapsed = !axis.collapsed;
        state.configuration = {
          ...configuration,
          configData: {
            ...configuration.configData,
            axes: configuration.configData.axes.map((a) => (a.id === axisId ? axis : a))
          }
        };
      }
    },

    updateAxisMotorType: (state, action) => {
      const { axisId, motorType } = action.payload;
      const { configuration } = state;
      const axis = configuration?.configData?.axes?.find((a) => a.id === axisId);

      if (axis) {
        axis.motorType = motorType;
        state.configuration = {
          ...configuration,
          configData: {
            ...configuration.configData,
            axes: configuration.configData.axes.map((a) => (a.id === axisId ? axis : a))
          }
        };
      }
    },

    deleteAxis: (state, action) => {
      const { axisId } = action.payload;
      const { configuration } = state;
      const axes = configuration?.configData?.axes?.filter((a) => a.id !== axisId);

      state.configuration = {
        ...configuration,
        configData: {
          ...configuration.configData,
          axes
        }
      };
    },

    duplicateAxis: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { axisId } = action.payload;
      const { configuration } = state;

      const axisToDuplicate = configuration.configData.axes.find((a) => a.id === axisId);

      const newId = configuration.configData.axes.reduce((max, axis) => Math.max(max, axis.id), 0) + 1;
      const newAxis = {
        ...axisToDuplicate,
        id: newId,
        name: `${t("msizer.ui.specify.name_duplication_prefix")} ${axisToDuplicate.name}`
      };

      configuration.configData.axes.push(newAxis);
      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    // set a bundle for an axis
    setBundle: (state, action) => {
      const { axisId, bundle } = action.payload;
      const { configuration } = state;
      const axis = configuration?.configData?.axes?.find((a) => a.id === axisId);

      if (axis) {
        axis.bundle = bundle;
        state.configuration = {
          ...configuration,
          configData: {
            ...configuration.configData,
            axes: configuration.configData.axes.map((a) => (a.id === axisId ? axis : a))
          }
        };
      }
    },
    setSncWidgetOutputReducer: (state, action) => {
      const { axisId, sncWidgetOutput } = action.payload;
      const { configuration } = state;
      const axis = configuration?.configData?.axes?.find((a) => a.id === axisId);

      if (axis) {
        axis.sncWidgetOutput = sncWidgetOutput;
        state.configuration = {
          ...configuration,
          configData: {
            ...configuration.configData,
            axes: configuration.configData.axes.map((a) => (a.id === axisId ? axis : a))
          }
        };
      }
    }
  }
});

export const {
  // LOGIC
  setIntegratedDrivesConfiguration,
  resetIntegratedDrives,
  setIntegratedDrivesPossibleFiltersValues,
  setIntegratedDrivesCurrentStep,
  setIntegratedDrivesConfigData,
  setReadOnlyMode,
  setIntegratedDrivesSavedConfigurations,
  setIntegratedDrivesAxes,
  addNewIntegratedDrivesAxis,

  // Update Axis infos
  updateAxisName,
  setIntegratedDrivesAxisFilters,
  collapseAxis,
  updateAxisMotorType,
  deleteAxis,
  duplicateAxis,
  setBundle,
  setSncWidgetOutputReducer
} = integrated_drives.actions;

// LOGIC
export const selectDefaultIntegratedDrivesConfiguration = (state) => state.integrated_drives.defaultConfiguration;
export const selectIntegratedDrivesConfiguration = (state) => state.integrated_drives.configuration;
export const selectIntegratedDrivesCurrentStep = (state) => state.integrated_drives.currentStep;
export const selectIntegratedDrivesSteps = (state) => state.integrated_drives.steps;
export const selectReadOnlyMode = (state) => state.integrated_drives.readOnlyMode;
export const selectIntegratedDrivesSavedConfigurations = (state) => state.integrated_drives.savedConfigurations;

export const selectIntegratedDrivesAxes = (state) => state.integrated_drives.configuration?.configData?.axes;

export default integrated_drives.reducer;
